/**
 * The call — a Decision read as one sentence with its nine slots, and a blank
 * wherever the record is silent (owner ruling, Wes, 2026-10-28). "Copy the
 * call" is the stranger test: hand the sentence to someone who was not there.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { AppCard, Eyebrow, StatusChip } from '@/apps/components/primitives';
import type { Decision } from '@/types/prioritization/decisions';
import { buildCall, type Call } from '@/lib/call/callSentence';
import { fetchCallFacts } from '@/lib/call/callService';

const BLANK = /(\[[^\]]+: not on the record\])/g;

function SentenceText({ sentence }: { sentence: string }) {
  const parts = sentence.split(BLANK);
  return (
    <p className="font-body text-sm leading-relaxed text-[var(--text-pri)]">
      {parts.map((p, i) =>
        BLANK.test(p) ? (
          <span key={i} className="rounded-sm bg-[var(--mustard-dim)] px-1 text-[var(--mustard)]" title="The record is silent here.">
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </p>
  );
}

export interface CallSentenceProps {
  decision: Decision;
  decisions: readonly Decision[];
  relatedNames?: string[];
  /**
   * Drawn beside a standing (a rank, a list state) rather than on the decision's
   * own page: the sentence and its chips, with the nine slots folded under a
   * disclosure and no card of its own (owner, 2026-10-30: a standing is never
   * drawn alone).
   */
  compact?: boolean;
  /** The eyebrow over the sentence; "The call" unless the host names the moment. */
  eyebrow?: string;
}

export function CallSentence({ decision, decisions, relatedNames, compact = false, eyebrow = 'The call' }: CallSentenceProps) {
  const [call, setCall] = useState<Call | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const namesKey = useMemo(() => (relatedNames ?? []).join('|'), [relatedNames]);

  useEffect(() => {
    let cancelled = false;
    setCall(null);
    setFailed(false);
    fetchCallFacts(decision, decisions, namesKey ? namesKey.split('|') : [])
      .then((facts) => { if (!cancelled) setCall(buildCall(facts)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [decision, decisions, namesKey]);

  const copy = async () => {
    if (!call) return;
    try {
      await navigator.clipboard.writeText(call.sentence);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const slots = call ? (
    <dl className={`grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[8rem_1fr] ${compact ? 'mt-2' : 'mt-3 border-t border-[var(--rule)] pt-3'}`}>
      {call.slots.map((s) => (
        <div key={s.key} className="contents">
          <dt className="font-body text-xs text-[var(--text-ter)]">
            <span className="font-medium text-[var(--text-sec)]">{s.label}</span>
            <span className="block">{s.question}</span>
          </dt>
          <dd className="font-body text-sm">
            {s.value !== null ? (
              <span className="text-[var(--text-pri)]">{s.value}</span>
            ) : (
              <span className="text-[var(--mustard)]">not on the record</span>
            )}
            {s.detail ? <span className="block text-xs text-[var(--text-ter)]">{s.detail}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  ) : null;

  const body = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Eyebrow>{eyebrow}</Eyebrow>
          {call ? (
            <>
              <StatusChip label={`${call.filled} of 9 slots on the record`} tone={call.whole ? 'green' : 'mustard'} />
              {call.agreement ? <StatusChip label="Agreement" tone="green" /> : null}
              {call.lapsed ? <StatusChip label="Lapsed" tone="coral" /> : null}
            </>
          ) : null}
        </div>
        <button
          type="button"
          onClick={copy}
          disabled={!call}
          className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50 dark:hover:bg-[var(--hover-tint)]"
          title="The stranger test: hand this to someone who was not there."
        >
          {copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy the call'}
        </button>
      </div>

      <div className="mt-2">
        {call ? (
          <SentenceText sentence={call.sentence} />
        ) : failed ? (
          <p className="font-body text-sm text-[var(--text-ter)]">The call could not be read from the record.</p>
        ) : (
          <p className="font-body text-sm text-[var(--text-ter)]">Reading the record…</p>
        )}
      </div>

      {call && compact ? (
        <details className="mt-2">
          <summary className="cursor-pointer font-body text-xs font-medium text-[var(--text-sec)] hover:underline">The nine slots</summary>
          {slots}
        </details>
      ) : (
        slots
      )}
    </>
  );

  if (compact) {
    return <div data-testid="call-sentence-compact">{body}</div>;
  }
  return <AppCard>{body}</AppCard>;
}
